'use client';

/**
 * CookieConsentContext — global cookie / analytics consent system
 *
 * Persists the visitor's consent choice in localStorage and, on acceptance,
 * dynamically loads the third-party analytics scripts (GTM + Clarity) so they
 * are never executed before the user has given explicit consent.
 *
 * Possible consent states:
 *   'pending'  — no choice stored yet (first visit or cleared storage)
 *   'accepted' — visitor accepted; analytics scripts are loaded
 *   'rejected' — visitor declined; no analytics scripts are loaded
 *
 * Usage: consume via the `useCookieConsent` hook — never import this context
 * directly in components.
 */

import { createContext, useEffect, useState, ReactNode } from 'react';
import { loadGTM } from '@/lib/analytics/gtm';
import { loadClarity } from '@/lib/analytics/clarity';

type ConsentStatus = 'pending' | 'accepted' | 'rejected';

interface CookieConsentContextType {
  /** Current consent state for the session. */
  consentStatus: ConsentStatus;
  /** Accept cookies: persists choice, loads analytics scripts. */
  acceptCookies: () => void;
  /** Reject cookies: persists choice, analytics scripts remain unloaded. */
  rejectCookies: () => void;
}

export const CookieConsentContext =
  createContext<CookieConsentContextType | null>(null);

/** localStorage key used to persist the consent choice across sessions. */
const CONSENT_KEY = 'cookie_consent';

/**
 * Wrap the application (or a subtree) with CookieConsentProvider to enable
 * consent-gated analytics loading. Typically placed near the root layout.
 *
 * Children are intentionally not rendered until localStorage has been read
 * (`isLoaded` flag) to prevent a flash of the consent banner on returning
 * visitors who already accepted or rejected cookies.
 */
export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');

  /**
   * Guards child rendering until the stored consent value has been read.
   * Avoids showing the cookie banner briefly to visitors who already chose.
   */
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'accepted' || stored === 'rejected') {
      setConsentStatus(stored);
      // Re-load analytics for returning visitors who previously accepted.
      // loadGTM / loadClarity are idempotent and safe to call on every mount.
      if (stored === 'accepted') {
        loadGTM();
        loadClarity();
      }
    }
    setIsLoaded(true);
  }, []);

  const acceptCookies = () => {
    setConsentStatus('accepted');
    localStorage.setItem(CONSENT_KEY, 'accepted');
    loadGTM();
    loadClarity();
  };

  const rejectCookies = () => {
    setConsentStatus('rejected');
    localStorage.setItem(CONSENT_KEY, 'rejected');
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <CookieConsentContext.Provider
      value={{ consentStatus, acceptCookies, rejectCookies }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}
